import React, { useState, useEffect, useContext } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  Box,
  CircularProgress,
  IconButton,
  Checkbox,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { CollectionInterface, CardTypes } from "ts-interfaces";
import { AuthContext } from "../auth/Auth";
import { useCollectionDetailed } from "../global-state/useCollectionDetailed";
import { ExistingCard } from "../projects_v2/components/ExistingCard";
import { usePermissions, firebaseHelper } from "ui";
import { useLoginState } from "../studioxr/auth/LoginState";
import { produce } from "immer";
import { encodeHTML } from "../libs/util";
import { useMattoState } from "../MattoState";
import { duplicateCollectionScheme, associateCollectionWithProject } from "../v2/apis/collectionsSchemeApi";
import { DeleteComponentModal } from "../projects_v2/components/DeleteComponentModal";
import { ShareComponentModal } from "../projects_v2/components/ShareComponentModal";
import { AssociatedProjectModal } from "../projects_v2/components/AssociatedProjectModal";

interface SelectCollectionForDefaultModalProps {
  open: boolean;
  onClose: () => void;
  onSelectCollection: (collectionId: string) => void;
  currentBoardId: string;
}

export const SelectCollectionForDefaultModal = ({
  open,
  onClose,
  onSelectCollection,
  currentBoardId,
}: SelectCollectionForDefaultModalProps) => {
  const { currentUser } = useContext(AuthContext);
  const [selectedCollectionId, setSelectedCollectionId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  
  // State for modals
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showAssociatedProjectModal, setShowAssociatedProjectModal] = useState(false);
  const [selectedCollection, setSelectedCollection] = useState<CollectionInterface>({} as CollectionInterface);
  
  // Get collections from the detailed state
  const {
    allCollections,
    isLoading,
    fetchCollections,
    initialFetchDone
  } = useCollectionDetailed();
  
  const { permissions } = usePermissions(currentUser?.uid, firebaseHelper.db);
  const addOrUpdateCollection = useLoginState((state) => state.addOrUpdateCollection);
  const setSnackbarMessage = useMattoState((state) => state.setSnackbarMessage);

  useEffect(() => {
    if (open && currentUser?.uid && !initialFetchDone) {
      fetchCollections(currentUser.uid, permissions);
    }
  }, [open, currentUser?.uid, initialFetchDone, fetchCollections, permissions]);

  const handleCollectionSelect = (collection: CollectionInterface) => {
    // Toggle selection: if already selected, deselect it; otherwise select it
    setSelectedCollectionId(
      selectedCollectionId === collection.id ? null : collection.id
    );
  };

  const handleConfirm = async () => {
    if (!selectedCollectionId || !currentBoardId || !currentUser?.uid) {
      return;
    }

    setSaving(true);

    try {
      // Find the selected collection
      const selectedCollection = allCollections.find(col => col.id === selectedCollectionId);
      if (!selectedCollection) {
        throw new Error("Selected collection not found");
      }

      // Create the board URL (same format as CollectionModal.tsx)
      const boardUrl = `${window.location.origin}/project/${currentBoardId}`;

      // Update the collection with the new associated board URL
      const updatedCollection: CollectionInterface = produce(selectedCollection, (draft: CollectionInterface) => {
        draft.associatedBoardUrl = encodeHTML(boardUrl);
        draft.updatedAt = Date.now();
      });

      // Save the updated collection using the same method as CollectionModal
      await addOrUpdateCollection(updatedCollection, null);

      // Call the parent callback
      onSelectCollection(selectedCollectionId);
      
      // Close the modal
      handleClose();

    } catch (error) {
      console.error("Error setting board as default for collection:", error);
      // You might want to show an error message here
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setSelectedCollectionId(null);
    setSaving(false);
    onClose();
  };

  // Real implementations for ExistingCard props
  const handleEdit = (collection: CollectionInterface) => {
    useMattoState.getState().setModal({
      name:'collection', 
      id: collection.id, 
      associatedProjectsList: [], 
      showShareCollection: false, 
      onSave: (updatedCollection: CollectionInterface) => {
        // Update the collection in the detailed state
        const updatedCollections = allCollections.map(col => 
          col.id === updatedCollection.id ? updatedCollection : col
        );
        // You may need to update the collections state here
        setSnackbarMessage('Collection updated successfully');
      }
    });    
  };

  const handleCopy = async (e: React.MouseEvent, collection: CollectionInterface) => {
    e.stopPropagation();
    e.preventDefault();
    setSnackbarMessage('Duplicating collection...');
    try {
      const duplicatedCollection = await duplicateCollectionScheme(collection);
      setSnackbarMessage('Collection duplicated successfully');
      // Optionally refresh collections
      if (currentUser?.uid) {
        fetchCollections(currentUser.uid, permissions);
      }
    } catch (error) {
      console.error('Error duplicating collection:', error);
      setSnackbarMessage('Failed to duplicate collection');
    }
  };

  const openDeleteModal = (e: React.MouseEvent, collection: CollectionInterface) => {
    e.stopPropagation();
    setSelectedCollection(collection);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    try {
      setSnackbarMessage('Deleting collection...');
      await useLoginState.getState().deleteCollection(selectedCollection, currentUser?.uid);
      setShowDeleteModal(false);
      setSelectedCollection({} as CollectionInterface);
      setSnackbarMessage('Collection deleted successfully');
      // Refresh collections
      if (currentUser?.uid) {
        fetchCollections(currentUser.uid, permissions);
      }
    } catch (error) {
      console.error('Error deleting collection:', error);
      let errorMessage = 'Failed to delete collection';
      if (error.message) {
        if (error.message.includes('permission')) {
          errorMessage = 'You don\'t have permission to delete this collection';
        } else if (error.message.includes('not found')) {
          errorMessage = 'Collection not found or already deleted';
        } else {
          errorMessage = error.message;
        }
      }
      setSnackbarMessage(errorMessage);
      setShowDeleteModal(false);
      setSelectedCollection({} as CollectionInterface);
    }
  };

  const openShareModal = (e: React.MouseEvent, collection: CollectionInterface) => {
    e.stopPropagation();
    setSelectedCollection(collection);
    setShowShareModal(true);
  };

  const handleShareCollection = () => {
    if (selectedCollection) {
      navigator.clipboard.writeText(`${window.location.origin}/project/new?collectionID=${selectedCollection.id}`);
      setSnackbarMessage(`Collection URL copied to clipboard`);
    }
  };

  const openAssociatedProjectModal = (e: React.MouseEvent, collection: CollectionInterface) => {
    e.stopPropagation();
    setSelectedCollection(collection);
    setShowAssociatedProjectModal(true);
  };

  const handleAssociatedProjectChange = async ({ newProjectId, oldProjectId, collection = null }: { newProjectId: string | null, oldProjectId: string | null, collection?: CollectionInterface | null }) => {
    const targetCollection = collection || selectedCollection;
    if (!targetCollection) return;
    
    try {
      await associateCollectionWithProject(targetCollection.id, newProjectId, currentUser.uid, permissions);
      
      setShowAssociatedProjectModal(false);
      setSnackbarMessage('Collection association updated successfully');
      // Refresh collections
      if (currentUser?.uid) {
        fetchCollections(currentUser.uid, permissions);
      }
    } catch (error) {
      console.error('Error updating collection association:', error);
      setSnackbarMessage(error.message || 'Failed to update collection association');
    }
  };

  const handleRemoveFromProject = (collection: CollectionInterface) => {
    setSnackbarMessage('Removing collection from project...');
    setSelectedCollection(collection);
    handleAssociatedProjectChange({
      newProjectId: null,
      oldProjectId: collection?.associatedProjectId || null,
      collection: collection
    });
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xl" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6">
          Select Collection to Make Board Default
        </Typography>
        <IconButton onClick={handleClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ minHeight: '400px' }}>
        {isLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
            <CircularProgress />
          </div>
        ) : allCollections.length === 0 ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
            <Typography variant="body1" color="text.secondary">
              No collections found. Create a collection first to make this board default for it.
            </Typography>
          </div>
        ) : (
          <Grid 
            container 
            sx={{ 
              display: 'grid',
              gridTemplateColumns: {
                xs: 'repeat(1, 385px)',
                '@media (min-width: 860px) and (max-width: 1269px)': {
                  gridTemplateColumns: 'repeat(2, 385px)'
                },
                '@media (min-width: 1270px) and (max-width: 1679px)': {
                  gridTemplateColumns: 'repeat(3, 385px)'
                },
                '@media (min-width: 1680px)': {
                  gridTemplateColumns: 'repeat(4, 385px)'
                }
              },
              gap: 3,
              justifyContent: 'center',
              '& .MuiGrid-item': {
                padding: 0,
                width: '385px'
              }
            }}
          >
            {allCollections
              .sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0))
              .map((collection: CollectionInterface) => (
              <Grid 
                key={collection.id} 
                item 
                sx={{ 
                  position: 'relative',
                  cursor: 'pointer',
                  width: '385px'
                }}
              >
                <Checkbox
                  checked={selectedCollectionId === collection.id}
                  onChange={() => handleCollectionSelect(collection)}
                  aria-label={collection.name}
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    zIndex: 1,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    borderRadius: '4px',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    }
                  }}
                />
                <ExistingCard 
                  data={{
                    ...collection,
                    name: collection?.name,
                    thumbnail: collection?.thumbnails,
                    updatedAt: collection?.updatedAt
                  }} 
                  designFirms={[]} 
                  teamMembers={[]} 
                  type={CardTypes.Collections}
                  onClick={() => handleCollectionSelect(collection)} 
                  onEdit={() => handleEdit(collection)}
                  onDelete={(e) => openDeleteModal(e, collection)} 
                  onShare={(e) => openShareModal(e, collection)}
                  onCopy={(e) => handleCopy(e, collection)} 
                  onAssociatedProjectChange={(e) => openAssociatedProjectModal(e, collection)}
                  onRemoveFromProject={() => handleRemoveFromProject(collection)}
                  cardSize="default"
                  hasViewerAccess={false}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={!selectedCollectionId || saving}
        >
          {saving ? 'Setting as Default...' : 'Make Default'}
        </Button>
      </DialogActions>
      
      {/* Additional Modals */}
      <DeleteComponentModal 
        open={showDeleteModal} 
        setOpen={setShowDeleteModal} 
        onDelete={handleDelete} 
      />
      
      <ShareComponentModal 
        name={selectedCollection.name} 
        open={showShareModal} 
        setOpen={setShowShareModal} 
        onShare={handleShareCollection} 
      />
      
      <AssociatedProjectModal 
        open={showAssociatedProjectModal} 
        onClose={() => setShowAssociatedProjectModal(false)}  
        onSave={handleAssociatedProjectChange} 
        data={selectedCollection} 
        type={CardTypes.Collections} 
      />
    </Dialog>
  );
}; 
